import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import {
  useOrderSchedule,
  useCreateOrderSchedule,
  useUpdateOrderSchedule,
} from '../../hooks/useApi';
import type { OrderScheduleInput, RecurrenceType } from '../../types';

const recurrenceOptions: Array<{ value: RecurrenceType; label: string }> = [
  { value: 'DAILY', label: 'Dagligt' },
  { value: 'WEEKLY', label: 'Ugentligt' },
  { value: 'BIWEEKLY', label: 'Hver 2. uge' },
  { value: 'EVERY_X_DAYS', label: 'Hver X dage' },
  { value: 'SPECIFIC_WEEKDAYS', label: 'Bestemte ugedage' },
];

const weekdayOptions = [
  { value: 1, label: 'Man' },
  { value: 2, label: 'Tir' },
  { value: 3, label: 'Ons' },
  { value: 4, label: 'Tor' },
  { value: 5, label: 'Fre' },
  { value: 6, label: 'Lør' },
  { value: 7, label: 'Søn' },
];

interface ScheduleFormData {
  name: string;
  recurrenceType: RecurrenceType;
  intervalDays: string;
  weekdays: number[];
  weekdaysOnly: boolean;
  startDate: string;
  endDate: string;
  leadTimeDays: string;
}

export default function OrderScheduleForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();

  const { data: existingSchedule, isLoading: loadingSchedule } = useOrderSchedule(id || '');
  const createSchedule = useCreateOrderSchedule();
  const updateSchedule = useUpdateOrderSchedule();

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const { register, handleSubmit, control, reset, watch, setValue } = useForm<ScheduleFormData>({
    defaultValues: {
      name: '',
      recurrenceType: 'WEEKLY',
      intervalDays: '7',
      weekdays: [],
      weekdaysOnly: false,
      startDate: '',
      endDate: '',
      leadTimeDays: '1',
    },
  });

  const watchRecurrenceType = watch('recurrenceType');
  const watchWeekdays = watch('weekdays');
  const isPending = createSchedule.isPending || updateSchedule.isPending;

  useEffect(() => {
    if (isEdit && existingSchedule) {
      const s = existingSchedule;
      reset({
        name: s.name,
        recurrenceType: s.recurrenceType as RecurrenceType,
        intervalDays: s.intervalDays != null ? String(s.intervalDays) : '7',
        weekdays: s.weekdays || [],
        weekdaysOnly: s.weekdaysOnly,
        startDate: s.startDate ? s.startDate.split('T')[0] : '',
        endDate: s.endDate ? s.endDate.split('T')[0] : '',
        leadTimeDays: String(s.leadTimeDays),
      });
    }
  }, [isEdit, existingSchedule, reset]);

  async function onSubmit(data: ScheduleFormData) {
    const payload: OrderScheduleInput = {
      name: data.name,
      recurrenceType: data.recurrenceType,
      intervalDays: data.recurrenceType === 'EVERY_X_DAYS' ? parseInt(data.intervalDays) || 7 : null,
      weekdays: ['WEEKLY', 'BIWEEKLY', 'SPECIFIC_WEEKDAYS'].includes(data.recurrenceType) ? data.weekdays : [],
      weekdaysOnly: data.weekdaysOnly,
      startDate: data.startDate,
      endDate: data.endDate,
      leadTimeDays: parseInt(data.leadTimeDays) || 1,
    };

    try {
      if (isEdit) {
        await updateSchedule.mutateAsync({ id: id!, data: payload });
        setSnackbar({ open: true, message: 'Ordreskema opdateret', severity: 'success' });
        navigate(`/ordreskemaer/${id}`);
      } else {
        const result = await createSchedule.mutateAsync(payload);
        setSnackbar({ open: true, message: 'Ordreskema oprettet', severity: 'success' });
        navigate(`/ordreskemaer/${result.id}`);
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Fejl ved gemning', severity: 'error' });
    }
  }

  function toggleWeekday(day: number) {
    const current = watchWeekdays || [];
    const next = current.includes(day) ? current.filter(d => d !== day) : [...current, day].sort();
    setValue('weekdays', next);
  }

  if (isEdit && loadingSchedule) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/ordreskemaer')}>Tilbage</Button>
        <Typography variant="h5" fontWeight={600}>{isEdit ? 'Rediger ordreskema' : 'Nyt ordreskema'}</Typography>
        <Box sx={{ flex: 1 }} />
        <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={isPending}>
          {isPending ? 'Gemmer...' : isEdit ? 'Gem ændringer' : 'Opret'}
        </Button>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Skema & gentagelse</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Skemanavn *" {...register('name', { required: true })} />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Controller
                name="recurrenceType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Gentagelse *</InputLabel>
                    <Select label="Gentagelse *" {...field}>
                      {recurrenceOptions.map(o => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {watchRecurrenceType === 'EVERY_X_DAYS' && (
              <Grid item xs={6} sm={2}>
                <TextField fullWidth label="Interval (dage)" type="number" {...register('intervalDays')} />
              </Grid>
            )}

            {['DAILY', 'WEEKLY', 'BIWEEKLY', 'EVERY_X_DAYS', 'SPECIFIC_WEEKDAYS'].includes(watchRecurrenceType) && (
              <Grid item xs={12} sm={5}>
                <Typography variant="caption" color="text.secondary">Ugedage {watchRecurrenceType === 'SPECIFIC_WEEKDAYS' ? '*' : '(valgfrit — alle hvis ingen valgt)'}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {weekdayOptions.map(d => (
                    <Chip
                      key={d.value}
                      label={d.label}
                      size="small"
                      color={(watchWeekdays || []).includes(d.value) ? 'primary' : 'default'}
                      onClick={() => toggleWeekday(d.value)}
                      variant={(watchWeekdays || []).includes(d.value) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Grid>
            )}

            <Grid item xs={6} sm={2}>
              <TextField fullWidth label="Fra dato *" type="date" InputLabelProps={{ shrink: true }}
                {...register('startDate', { required: true })} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth label="Til dato *" type="date" InputLabelProps={{ shrink: true }}
                {...register('endDate', { required: true })} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth label="Opret X dage før" type="number" {...register('leadTimeDays')}
                helperText="Hvor mange dage i forvejen ordren oprettes" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
